import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { classNames } from "../../../ui/styles/classNames";
import type { ItemAtVersion } from "../../threads/prepare";
import { CardMetaLine, CardTypeLabel } from "./CardParts";
import EditItemButton from "./EditItemButton";
import { inputCardMeta, type InputCardFacts } from "./inputCardFacts";

interface InputCardProps {
  title: string;
  facts: InputCardFacts;
  /** "required" or "optional", said before its type; `null` where it doesn't apply. */
  need?: string | null;
  /** Shown on hover over its name. */
  description?: string;
  /** Opens the edit Thread on the card's item (F51); `null`: the view edits nothing. */
  onEdit: ((item: ItemAtVersion) => void) | null;
  className?: string;
  /** Its value: a preview, a field or a picker. */
  children?: ReactNode;
}

/**
 * One input card in a list (F61, F56): its name, need and type, its version and origin, Edit
 * when it names an item, and its value below. The Workflow graph draws the same facts on its
 * own fixed-size cards (`nodeText`).
 */
export default function InputCard({ title, facts, need = null, description, onEdit, className, children }: InputCardProps) {
  const { t } = useTranslation();
  const meta = inputCardMeta(facts, t);
  const { edit } = facts;
  // "Prompt · Prompt" says it once, as its name.
  const type = facts.type.toLocaleLowerCase() === title.toLocaleLowerCase() ? null : facts.type;
  return (
    <li className={classNames("app-card min-w-0 space-y-1.5 p-2 text-sm", className)}>
      <div className="flex flex-wrap items-baseline gap-x-1.5" title={description === "" ? undefined : description}>
        <span className="font-medium">{title}</span>
        {need === null ? null : <span className="app-text-muted text-xs">{type === null ? need : `${need} ·`}</span>}
        {type === null ? null : <CardTypeLabel type={type} hint={facts.typeHint ?? null} />}
        {edit === null || onEdit === null ? null : <EditItemButton item={edit} title={edit.name} onEdit={onEdit} />}
      </div>
      {meta === null ? null : <CardMetaLine text={meta} />}
      {facts.shownAs === undefined || facts.shownAs === null ? null : <CardMetaLine text={facts.shownAs} />}
      {children}
    </li>
  );
}
